import React, { useState, useCallback, useMemo } from 'react';
import { Table, Button, Space, Tag, Modal, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useLearningCenterFaqCategoryList } from './hooks/useLearningCenterFaqConfigList';
import FaqCategoryDrawer from './components/FaqCategoryDrawer';
import FaqArticleDrawer from './components/FaqArticleDrawer';
import FaqArticleSubTable from './components/FaqArticleSubTable';
import { DELETE_DRIVER_LEARNING_CENTER_FAQ_CATEGORY, TOGGLE_DRIVER_LEARNING_CENTER_FAQ_NODE_ENABLED } from './graphql/mutations';
import { DriverLearningCenterFaqNode } from './graphql/types';
import TablePaginationBar from '../../../components/TablePaginationBar';
import ParcelFilterStyle from '../../delivery/components/ParcelFilterStyle';
import InputFilterItem from '../../delivery/components/InputFilterItem';
import { getSmartLabelClassName } from '../../../utils/labelWidthUtils';
import { formatEpochToDateTime } from '../../../utils/dateTimeUtils';
import { resolveLearningCenterErrorMessage } from './utils/errorMessage';

const LearningCenterConfigPage: React.FC = () => {
  const { t } = useTranslation();
  // 筛选项草稿（改完后点「筛选」才生效）
  const [draftKeyword, setDraftKeyword] = useState('');
  const [draftIsEnabled, setDraftIsEnabled] = useState<boolean | null>(null);
  // 实际查询条件
  const [keyword, setKeyword] = useState('');
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [subTableRefreshTokens, setSubTableRefreshTokens] = useState<Record<string, number>>({});

  const [categoryDrawerVisible, setCategoryDrawerVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<DriverLearningCenterFaqNode | null>(null);

  const [articleDrawerVisible, setArticleDrawerVisible] = useState(false);
  const [editingArticle, setEditingArticle] = useState<DriverLearningCenterFaqNode | null>(null);
  const [articleDefaultParentId, setArticleDefaultParentId] = useState<string | null>(null);

  const { categories, total, loading, refetch } = useLearningCenterFaqCategoryList(pageNum, pageSize, keyword, isEnabled);

  const [deleteCategory] = useMutation(DELETE_DRIVER_LEARNING_CENTER_FAQ_CATEGORY);
  const [toggleEnabled] = useMutation(TOGGLE_DRIVER_LEARNING_CENTER_FAQ_NODE_ENABLED);

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: c.titleZh || c.titleEn || c.slug })),
    [categories]
  );

  const handleApplyFilter = useCallback(() => {
    setKeyword(draftKeyword.trim());
    setIsEnabled(draftIsEnabled);
    setPageNum(1);
    setExpandedRowKeys([]);
  }, [draftKeyword, draftIsEnabled]);

  const handleResetFilter = useCallback(() => {
    setDraftKeyword('');
    setDraftIsEnabled(null);
    setKeyword('');
    setIsEnabled(null);
    setPageNum(1);
    setExpandedRowKeys([]);
  }, []);

  const handleAddCategory = useCallback(() => {
    setEditingCategory(null);
    setCategoryDrawerVisible(true);
  }, []);

  const handleEditCategory = useCallback((category: DriverLearningCenterFaqNode) => {
    setEditingCategory(category);
    setCategoryDrawerVisible(true);
  }, []);

  const handleAddArticle = useCallback((categoryId: string) => {
    setEditingArticle(null);
    setArticleDefaultParentId(categoryId);
    setArticleDrawerVisible(true);
  }, []);

  const handleEditArticle = useCallback((article: DriverLearningCenterFaqNode) => {
    setEditingArticle(article);
    setArticleDefaultParentId(article.parentId);
    setArticleDrawerVisible(true);
  }, []);

  const handleCategoryDrawerSuccess = useCallback(() => {
    setCategoryDrawerVisible(false);
    setEditingCategory(null);
    refetch();
  }, [refetch]);

  const handleArticleDrawerSuccess = useCallback((parentId: string, previousParentId?: string | null) => {
    setArticleDrawerVisible(false);
    setEditingArticle(null);
    // 刷新分类列表（问答数量等）
    refetch();
    // 强制重新挂载对应分类下的问答子表，否则已展开的子表不会自动 refetch
    const idsToRefresh = new Set<string>([parentId]);
    if (previousParentId && previousParentId !== parentId) {
      idsToRefresh.add(previousParentId);
    }
    setExpandedRowKeys((prev) => {
      const next = new Set(prev);
      next.add(parentId);
      return Array.from(next);
    });
    setSubTableRefreshTokens((prev) => {
      const next = { ...prev };
      idsToRefresh.forEach((id) => {
        next[id] = (next[id] ?? 0) + 1;
      });
      return next;
    });
  }, [refetch]);

  const handleToggleCategory = useCallback(async (category: DriverLearningCenterFaqNode) => {
    try {
      await toggleEnabled({ variables: { id: category.id, isEnabled: !category.isEnabled } });
      message.success(t('learningCenterConfig.messages.toggleSuccess'));
      refetch();
      // 分类禁用/启用会联动改变子问答的展示状态，若该分类当前处于展开态，
      // 强制重新挂载其 FaqArticleSubTable 以拉取最新数据，避免展示旧的本地状态。
      if (expandedRowKeys.includes(category.id)) {
        setSubTableRefreshTokens((prev) => ({ ...prev, [category.id]: (prev[category.id] ?? 0) + 1 }));
      }
    } catch (err: any) {
      const errorMessage = resolveLearningCenterErrorMessage(err, t);
      if (errorMessage) {
        message.error(errorMessage);
      }
    }
  }, [toggleEnabled, refetch, expandedRowKeys, t]);

  const handleDeleteCategory = useCallback((category: DriverLearningCenterFaqNode) => {
    Modal.confirm({
      title: t('learningCenterConfig.messages.deleteCategoryConfirmTitle'),
      content: t('learningCenterConfig.messages.deleteCategoryConfirmContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await deleteCategory({ variables: { id: category.id } });
          message.success(t('learningCenterConfig.messages.deleteSuccess'));
          refetch();
        } catch (err: any) {
          const errorMessage = resolveLearningCenterErrorMessage(err, t);
          if (errorMessage) {
            message.error(errorMessage);
          }
        }
      },
    });
  }, [deleteCategory, refetch, t]);

  const columns = [
    {
      title: t('learningCenterConfig.columns.category'),
      dataIndex: 'titleZh',
      key: 'titleZh',
      render: (value: string, record: DriverLearningCenterFaqNode) => value || record.titleEn || record.slug,
    },
    {
      title: t('learningCenterConfig.columns.sortOrder'),
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 90,
    },
    {
      title: t('learningCenterConfig.columns.status'),
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 100,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'default'}>
          {enabled ? t('learningCenterConfig.filter.enabled') : t('learningCenterConfig.filter.disabled')}
        </Tag>
      ),
    },
    {
      title: t('learningCenterConfig.columns.updatedBy'),
      dataIndex: 'updatedByName',
      key: 'updatedByName',
      width: 140,
      render: (value: string | null, record: DriverLearningCenterFaqNode) => value || record.updatedBy || '-',
    },
    {
      title: t('learningCenterConfig.columns.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (value: number | string | null) => formatEpochToDateTime(value),
    },
    {
      title: t('learningCenterConfig.columns.action'),
      key: 'action',
      width: 280,
      render: (_: any, record: DriverLearningCenterFaqNode) => (
        <Space>
          <Button type="link" onClick={() => handleEditCategory(record)}>{t('learningCenterConfig.buttons.edit')}</Button>
          <Button type="link" onClick={() => handleAddArticle(record.id)}>{t('learningCenterConfig.buttons.newArticle')}</Button>
          <Button type="link" onClick={() => handleToggleCategory(record)}>
            {record.isEnabled ? t('learningCenterConfig.buttons.disable') : t('learningCenterConfig.buttons.enable')}
          </Button>
          <Button type="link" danger onClick={() => handleDeleteCategory(record)}>{t('learningCenterConfig.buttons.delete')}</Button>
        </Space>
      ),
    },
  ];

  const filterLabels = useMemo(
    () => [
      String(t('learningCenterConfig.filter.keyword')),
      String(t('learningCenterConfig.filter.status')),
    ],
    [t]
  );

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-3">
      <div className="mb-2 pl-0 py-2">
        <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
          <div className="min-w-0">
            <InputFilterItem
              label={String(t('learningCenterConfig.filter.keyword'))}
              value={draftKeyword}
              onChange={(e) => setDraftKeyword(e.target.value)}
              placeholder={String(t('learningCenterConfig.filter.keywordPlaceholder'))}
              className="w-full"
              spanClassName={getSmartLabelClassName(filterLabels)}
            />
          </div>
          <div className="min-w-0">
            <ParcelFilterStyle
              label={t('learningCenterConfig.filter.status')}
              options={[
                { value: true, label: String(t('learningCenterConfig.filter.enabled')) },
                { value: false, label: String(t('learningCenterConfig.filter.disabled')) },
              ]}
              value={draftIsEnabled === null ? '' : String(draftIsEnabled)}
              onChange={(value: string | undefined) => {
                if (value === undefined || value === '') {
                  setDraftIsEnabled(null);
                  return;
                }
                setDraftIsEnabled(value === 'true');
              }}
              className="w-full"
              spanClassName={getSmartLabelClassName(filterLabels)}
              placeholder={String(t('learningCenterConfig.filter.all'))}
            />
          </div>
        </div>
        <div className="mt-3 mb-1 flex justify-between items-center pl-0">
          <div className="flex gap-4">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
              className="w-auto min-w-24"
            >
              <span className="font-bold">{t('learningCenterConfig.buttons.newCategory')}</span>
            </Button>
          </div>
          <div className="flex gap-4">
            <Button
              type="primary"
              onClick={handleApplyFilter}
              className="w-auto min-w-24"
            >
              <span className="font-bold">{t('learningCenterConfig.filter.filterButton')}</span>
            </Button>
            <Button
              type="default"
              onClick={handleResetFilter}
              className="w-auto min-w-24"
            >
              <span className="font-bold">{t('learningCenterConfig.filter.resetButton')}</span>
            </Button>
          </div>
        </div>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={categories}
        loading={loading}
        pagination={false}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
          expandedRowRender: (record: DriverLearningCenterFaqNode) => (
            <FaqArticleSubTable
              key={`${record.id}-${subTableRefreshTokens[record.id] ?? 0}`}
              categoryId={record.id}
              keyword={keyword}
              isEnabled={isEnabled}
              onEdit={handleEditArticle}
            />
          ),
        }}
      />

      <TablePaginationBar
        total={total}
        current={pageNum}
        pageSize={pageSize}
        onChange={(page, size) => {
          setPageNum(page);
          setPageSize(size);
        }}
      />

      <FaqCategoryDrawer
        visible={categoryDrawerVisible}
        category={editingCategory}
        onClose={() => setCategoryDrawerVisible(false)}
        onSuccess={handleCategoryDrawerSuccess}
      />

      <FaqArticleDrawer
        visible={articleDrawerVisible}
        article={editingArticle}
        categoryOptions={categoryOptions}
        defaultParentId={articleDefaultParentId}
        onClose={() => setArticleDrawerVisible(false)}
        onSuccess={handleArticleDrawerSuccess}
      />
    </div>
  );
};

export default LearningCenterConfigPage;
